import { useMemo, useState } from "react";
import { AppDialog } from "../../components/AppDialog";

export interface LinkOption {
  id: string;
  label: string;
}

interface Props {
  /** Dialog title, e.g. "Link State" / "Link City" / "Link Area". */
  title: string;
  /** Candidate rows — the UNLINKED existing records. */
  options: LinkOption[];
  /** The single data column's header ("State" / "City" / "Area"). */
  columnLabel: string;
  emptyText?: string;
  pageSize?: number;
  /** Per-row edit pencil (legacy parity). When set, a pencil column is shown. */
  onEdit?: (opt: LinkOption) => void;
  onOk: (selected: LinkOption[]) => void;
  onClose: () => void;
}

/**
 * The legacy XAF association "Link" popup, shared by the Country ▸ State ▸ City ▸ Area pickers
 * (owner request 2026-10-23, image 844). Search box + a select-all header checkbox + per-row
 * checkboxes + an edit pencil column + pagination (page size 10/20/50/100). OK links the whole
 * checked set. Selection persists across search and pages.
 */
export function LinkPickerDialog({ title, options, columnLabel, emptyText, pageSize = 20, onEdit, onOk, onClose }: Props) {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t ? options.filter((o) => o.label.toLowerCase().includes(t)) : options;
  }, [options, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / size));
  const curPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((curPage - 1) * size, curPage * size);
  const allFilteredChecked = filtered.length > 0 && filtered.every((o) => sel.has(o.id));

  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSel((s) => {
      const n = new Set(s);
      if (allFilteredChecked) filtered.forEach((o) => n.delete(o.id));
      else filtered.forEach((o) => n.add(o.id));
      return n;
    });

  return (
    <AppDialog
      open
      title={title}
      icon="ti-link"
      width={480}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ca-btn ca-btn-solid"
            onClick={() => onOk(options.filter((o) => sel.has(o.id)))}
            disabled={sel.size === 0}
          >
            OK
          </button>
        </>
      }
    >
      <div className="cd-lp-search">
        <label htmlFor="lp-search">Search</label>
        <input
          id="lp-search"
          placeholder="Text to search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>
      <table className="cd-state-tbl">
        <thead>
          <tr>
            <th className="cd-sel">
              <input type="checkbox" checked={allFilteredChecked} onChange={toggleAll} aria-label="select all" />
            </th>
            {onEdit && <th className="cd-edit" aria-label="edit" />}
            <th>{columnLabel}</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 ? (
            <tr>
              <td colSpan={onEdit ? 3 : 2} className="cd-empty">
                {emptyText ?? "No data to display"}
              </td>
            </tr>
          ) : (
            pageRows.map((o) => (
              <tr key={o.id} className={sel.has(o.id) ? "cd-rowsel" : undefined} onClick={() => toggle(o.id)}>
                <td className="cd-sel">
                  <input type="checkbox" checked={sel.has(o.id)} onChange={() => toggle(o.id)} onClick={(e) => e.stopPropagation()} />
                </td>
                {onEdit && (
                  <td className="cd-edit">
                    <button
                      type="button"
                      className="sd-link-edit"
                      title="Edit"
                      aria-label="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(o);
                      }}
                    >
                      <i className="ti ti-edit" />
                    </button>
                  </td>
                )}
                <td>{o.label}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="cd-lp-foot">
        <span>
          Page {curPage} of {totalPages} ({filtered.length} items)
        </span>
        <div className="cd-lp-pages">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage <= 1} aria-label="previous">
            ‹
          </button>
          <span>{curPage}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage >= totalPages} aria-label="next">
            ›
          </button>
        </div>
        <label>
          Page size:{" "}
          <select
            value={size}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
    </AppDialog>
  );
}
